/**
 * Client-side PIN validation rules.
 * Some run for every sponsor; the rest are gated on the sponsor's effective
 * PinConfig, delivered in context.policy (see below).
 *
 * Rule codes returned by validate() map to per-rule elements in templates:
 *   Card activation: PINMsg{RuleCode}   (e.g. PINMsgAllSame)
 *   Reset PIN:       pinRule{RuleCode}  (e.g. pinRuleAllSame) in #resetPinRulesList
 *
 * Contextual forbidden PINs are server-derived 4-digit candidates (dobPins,
 * phonePins, lastFourPins) passed via validate(pin, context) or
 * data-pin-validation-context on the form element.
 *
 * Sponsor-configurable rules (NANA-18). context.policy carries the sponsor's
 * effective PinConfig, resolved by member-api through customer -> brand
 * inheritance and delivered with the E6 PIN context:
 *
 *   validateRepeatedDigits -> ALL_SAME
 *   validateDob            -> DOB_PATTERN
 *   validateCardLastFour   -> LAST_FOUR
 *   validatePhone          -> PHONE
 *
 * A rule whose flag is false is neither enforced nor advertised in the rules
 * list. When policy is absent a flag reads as enabled: the server only renders
 * a PIN form once member-api has returned a context (which always carries a
 * policy), so a missing policy means a legacy or degraded render, and the
 * stricter reading is the safe one. E6 stays the authoritative weak-PIN
 * validator either way.
 *
 * DELIBERATELY UNIVERSAL: four numeric digits, PIN/confirmation equality, and
 * SEQUENTIAL. PinConfig has no sequential field, so sequential rejection
 * applies to every sponsor by decision rather than by omission -- see NANA-18.
 * Making it configurable would need a new PinConfig field on sponsor-api,
 * member-api's PinValidationPolicy, and the IVR contract.
 */
var SEQUENTIAL_PINS = new Set([
    '0123',
    '1234',
    '2345',
    '3456',
    '4567',
    '5678',
    '6789',
    '9876',
    '8765',
    '7654',
    '6543',
    '5432',
    '4321',
    '3210'
]);

var pinValidation = {
    rules: {
        ALL_SAME: 'AllSame',
        SEQUENTIAL: 'Sequential',
        DOB_PATTERN: 'DobPattern',
        LAST_FOUR: 'LastFour',
        PHONE: 'Phone'
    },

    /**
     * Validates a PIN against the universal rules plus whichever contextual rules
     * the sponsor's effective policy enables.
     * @param {string} pin
     * @param {object} [context] - { policy?, dobPins?, phonePins?, lastFourPins? }
     * @returns {{ valid: boolean, errors: string[] }}
     */
    validate: function (pin, context) {
        var errors = [];
        var ctx = context || {};

        if (!this.isExactlyFourDigits(pin)) {
            return { valid: false, errors: errors };
        }

        if (
            this.policyAllows(ctx.policy, 'validateRepeatedDigits') &&
            this.isAllSameDigits(pin)
        ) {
            errors.push(this.rules.ALL_SAME);
        }

        // Universal by decision, not by omission -- see the note above.
        if (this.isSequential(pin)) {
            errors.push(this.rules.SEQUENTIAL);
        }

        if (
            this.policyAllows(ctx.policy, 'validateDob') &&
            this.pinInList(pin, ctx.dobPins)
        ) {
            errors.push(this.rules.DOB_PATTERN);
        }

        if (
            this.policyAllows(ctx.policy, 'validateCardLastFour') &&
            this.pinInList(pin, ctx.lastFourPins)
        ) {
            errors.push(this.rules.LAST_FOUR);
        }

        if (
            this.policyAllows(ctx.policy, 'validatePhone') &&
            this.pinInList(pin, ctx.phonePins)
        ) {
            errors.push(this.rules.PHONE);
        }

        return { valid: errors.length === 0, errors: errors };
    },

    /**
     * Quick boolean check: does the PIN pass all rules?
     * @param {string} pin
     * @param {object} [context]
     */
    isValid: function (pin, context) {
        return this.validate(pin, context).valid;
    },

    /**
     * Parses data-pin-validation-context from a form and merges live last-four
     * input when the policy enforces the card-last-four rule.
     * @param {HTMLFormElement} [form]
     * @returns {{ policy?: object, dobPins?: string[], phonePins?: string[], lastFourPins?: string[] }}
     */
    readContextFromForm: function (form) {
        var ctx = {};
        if (!form) {
            return ctx;
        }

        // An HTMX card lookup can replace step two with a fresher context than
        // the one rendered on the parent form. Prefer that nested context so a
        // degraded initial "{}" value cannot mask the successful retry.
        var contextElement = form.querySelector(
            '[data-pin-validation-context]'
        );
        var raw = contextElement
            ? contextElement.getAttribute('data-pin-validation-context')
            : form.getAttribute('data-pin-validation-context');
        if (raw) {
            try {
                var parsed = JSON.parse(raw);
                if (parsed && typeof parsed === 'object') {
                    ctx = parsed;
                }
            } catch (e) {
                // ignore malformed JSON
            }
        }

        var lastFourInput = document.getElementById('cardLastFourDigitsInput');
        if (
            lastFourInput &&
            this.policyAllows(ctx.policy, 'validateCardLastFour')
        ) {
            var liveLastFour = lastFourInput.value
                .replace(/\D/g, '')
                .slice(0, 4);
            if (liveLastFour.length === 4) {
                ctx.lastFourPins = [liveLastFour];
            }
        }

        return ctx;
    },

    /**
     * True when the sponsor's effective policy enables a rule. An absent policy
     * or an absent flag reads as enabled -- see the note at the top of the file.
     * @param {object} [policy]
     * @param {string} flag
     */
    policyAllows: function (policy, flag) {
        if (!policy || typeof policy !== 'object') {
            return true;
        }
        if (typeof policy[flag] !== 'boolean') {
            return true;
        }
        return policy[flag];
    },

    /**
     * Hides rules list entries the sponsor's policy does not enforce, so a member
     * is never shown a requirement that would not reject their PIN. Universal
     * rules are always left visible.
     * @param {object} [context] - as returned by readContextFromForm
     * @param {string} [listId] - defaults to the reset PIN rules list
     */
    syncRuleList: function (context, listId) {
        var list = document.getElementById(listId || 'resetPinRulesList');
        if (!list) {
            return;
        }

        var policy = (context || {}).policy;
        var gated = {
            pinRuleAllSame: 'validateRepeatedDigits',
            pinRuleDobPattern: 'validateDob',
            pinRuleLastFour: 'validateCardLastFour',
            pinRulePhone: 'validatePhone'
        };

        for (var id in gated) {
            if (!Object.prototype.hasOwnProperty.call(gated, id)) {
                continue;
            }
            var item = document.getElementById(id);
            if (item) {
                item.hidden = !this.policyAllows(policy, gated[id]);
            }
        }
    },

    /**
     * PIN must be exactly 4 numeric digits.
     */
    isExactlyFourDigits: function (pin) {
        return /^\d{4}$/.test(pin);
    },

    /**
     * All digits are the same (e.g., 1111, 0000, 9999).
     */
    isAllSameDigits: function (pin) {
        return /^(\d)\1{3}$/.test(pin);
    },

    /**
     * Digits form a sequential sequence, ascending or descending (e.g., 1234, 9876).
     */
    isSequential: function (pin) {
        return SEQUENTIAL_PINS.has(pin);
    },

    /**
     * True when pin equals any value in a server-derived forbidden PIN list.
     * @param {string} pin
     * @param {string[]} [pins]
     */
    pinInList: function (pin, pins) {
        if (!pins || !pins.length) {
            return false;
        }
        for (var i = 0; i < pins.length; i++) {
            if (pin === pins[i]) {
                return true;
            }
        }
        return false;
    }
};

window.pinValidation = pinValidation;
